import { AccidentHistoryContext, useAccidentHistory } from '@/features/AccidentHistory';
import { DATE_INPUT_FORMAT, TODAY, TODAY_EOD } from '@/constants';
import { Datetime, changeState, saveFile } from '@/utils';
import { useCallback, useEffect, useState } from 'react';

import AccidentHistoryFiltersInfo from '@/features/AccidentHistory/components/AccidentHistoryFilters/AccidentHistoryFiltersInfo';
import { AccidentHistoryFormDefinitions } from '../AccidentHistoryForm/types';
import { AccidentIssueWithCacheAnyoneInjuredEnum } from '@/models/gen/graphql';
import AirlineIataDropdown from '@/components/AirlineDropdown';
import AirportDropdown from '@/components/AirportDropdown';
import { Button } from 'react-bootstrap';
import DateRangePicker from '@/components/DateRangePicker';
import EnumDropdown from '@/components/EnumDropdown';
import FormButton from '@/components/FormButton';
import { InputWithIcon } from '@/components/Input';
import PageNavigationDropdown from '@/common/PageNavigationDropdown';
import QueryFilters from '@/components/QueryFilters';
import { deleteAccidentIssue } from '../../api/services/deleteAccidentIssue';
import { deleteAccidentIssueDocument } from '../../api/services/deleteAccidentIssueDocument';
import { downloadAccidentIssueWithCacheCsv } from '../../api/services/downloadAccidentIssueCSV';
import { useAccidentHistoryDrawer } from '@/features/AccidentHistory/components/AccidentHistoryDrawer';
import useConfirmation from '@/hooks/useConfirmation';
import usePopState from '@/hooks/usePopState';
import { ServiceIssueFiltersType, useServiceIssueFilters } from '@/hooks/useServiceIssueFilters';

const initAccidentHistoryFiltersState: ServiceIssueFiltersType = {
  startDatetime: TODAY,
  endDatetime: TODAY_EOD,
  airportCode: [],
  tripServicerIataAirlineCodes: [],
  anyoneInjured: null,
};

// TODO: Add appropriate filters to this component based on the design when it is complete.
const AccidentHistoryFilters = (): JSX.Element => {
  const [{ loading, selected, accidents, search }, setState, { submitFilters }] = useAccidentHistory(
    ({ filters, loading, selected, accidents, search }) => ({
      filters,
      loading,
      selected,
      accidents,
      search,
    })
  );
  const filtersState = useServiceIssueFilters(({ state }) => state);
  const setFiltersState = useServiceIssueFilters(({ setState }) => setState);
  const saveFiltersState = useServiceIssueFilters(({ saveState }) => saveState);
  const rehydrateFiltersState = useServiceIssueFilters(({ rehydrate }) => rehydrate);
  const [lastQuery, setLastQuery] = useState<ServiceIssueFiltersType>(filtersState);
  const setAccidentHistoryDrawerState = useAccidentHistoryDrawer(({ setState }) => setState);
  const confirmDelete = useConfirmation();

  const handleChange = useCallback(
    (name: string) =>
      (value: unknown): void =>
        setFiltersState((current) => ({ ...current, [name]: value })),
    [setFiltersState]
  );

  const handleSubmit = useCallback((): void => {
    // get the latest state from closure to ensure it's always accurate
    const { state } = useServiceIssueFilters.getState();
    saveFiltersState();
    setLastQuery(state);
    submitFilters(state);
  }, [filtersState, saveFiltersState]);

  const handleReset = useCallback((): void => {
    const startDatetime = new Datetime().startOf('day').toString();
    const endDatetime = new Datetime().endOf('day').toString();
    const resetInput = { ...initAccidentHistoryFiltersState, startDatetime, endDatetime };
    setFiltersState(resetInput);
    setLastQuery(resetInput);
    setState((current) => ({ ...current, search: '' }));
    saveFiltersState();
    submitFilters(resetInput);
  }, [setState, saveFiltersState]);

  const handlePopState = useCallback(() => rehydrateFiltersState().then(handleSubmit), [rehydrateFiltersState, handleSubmit]);
  usePopState(handlePopState);

  const onChange = changeState<AccidentHistoryContext>(setState, {
    path: 'filters.*',
    handlers: {
      datetime: (value, setState) => {
        const [from, to] = (value?.target?.value?.split(' - ') || [TODAY, TODAY_EOD]).map((date: string): string =>
          new Datetime(date).toString()
        );
        setState((current) => ({
          ...current,
          filters: { ...current.filters, startDatetime: from, endDatetime: to },
        }));
      },
    },
  });

  const onCreate = () => {
    setAccidentHistoryDrawerState((current) => ({ ...current, show: true, data: AccidentHistoryFormDefinitions.initValue }));
  };

  const onDelete = async () => {
    confirmDelete()
      .then(async () => {
        try {
          // Get all of the documents and delete them first
          const documentIds = Array.from(selected.values())
            .map((issue) =>
              issue.documents.map((document) => ({
                id: document.id,
              }))
            )
            .flat();
          if (documentIds?.length > 0) {
            await deleteAccidentIssueDocument(documentIds);
          }
          const ids = Array.from(selected.keys()).map((id) => ({ id }));
          await deleteAccidentIssue(ids);
          setState((current) => ({
            ...current,
            selected: new Map(),
          }));
        } catch (err) {
          console.error('Error Deleting Incident.', err);
        } finally {
          return submitFilters?.(useServiceIssueFilters.getState().state);
        }
      })
      .catch(() => console.log('Cancelled delete'));
  };

  const onOpenSettings = () => {
    console.log('Open Settings');
  };

  const onExportToCSV = async () => {
    const { state: csvExportFilters } = useServiceIssueFilters.getState();
    const res = await downloadAccidentIssueWithCacheCsv([csvExportFilters]);
    if (!res) return;
    const blob = new Blob([res.data], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    saveFile(url, `AccidentIssueHistory-${new Datetime().format(DATE_INPUT_FORMAT)}.csv`);
  };

  useEffect((): void => {
    // submit filters on page load without resetting state
    handleSubmit();
  }, []);

  return (
    <>
      <QueryFilters className="AccidentHistoryFilters justify-content-between">
        <div className="d-flex gap-2">
          <QueryFilters.Control>
            <DateRangePicker
              name="dates"
              isDirty={filtersState.startDatetime !== lastQuery.startDatetime || filtersState.endDatetime !== lastQuery.endDatetime}
              value={[filtersState.startDatetime, filtersState.endDatetime]}
              onChange={(event) => {
                const [from, to] = event.target.value.split(' - ') || [null, null];
                handleChange('startDatetime')(from);
                handleChange('endDatetime')(to);
              }}
            />
          </QueryFilters.Control>
          <QueryFilters.Input>
            <AirportDropdown.Multi
              value={filtersState.airportCode}
              onChange={handleChange('airportCode')}
              options={{ locale: { 'Select...': 'Airports' } }}
            />
          </QueryFilters.Input>
          <QueryFilters.Input>
            <AirlineIataDropdown.Multi
              value={filtersState.tripServicerIataAirlineCodes}
              onChange={handleChange('tripServicerIataAirlineCodes')}
            />
          </QueryFilters.Input>
          <QueryFilters.Input>
            <EnumDropdown
              value={filtersState.anyoneInjured}
              onChange={handleChange('anyoneInjured')}
              enum={AccidentIssueWithCacheAnyoneInjuredEnum}
              options={{ locale: { 'Select...': 'Injuries?' } }}
            />
          </QueryFilters.Input>
          <QueryFilters.Control>
            <Button className="border-white" name="SUBMIT" variant="success" onClick={handleSubmit} disabled={loading}>
              {loading && <i className="fa fa-spinner fa-pulse" />}
              {!loading && <span>Go</span>}
            </Button>
            <Button name="RESET" variant="outline-secondary-subtle" onClick={handleReset}>
              Reset
            </Button>
          </QueryFilters.Control>
          <QueryFilters.Control>
            <InputWithIcon
              icon={<i className="sv sv-magnifier fs-4" />}
              name="search"
              value={search || ''}
              onChange={(value) => {
                setState((current) => ({ ...current, search: value }));
              }}
              placeholder="Search"
            />
          </QueryFilters.Control>
        </div>
        <div className="d-flex justify-content-end gap-2">
          <QueryFilters.Control>
            <FormButton
              maxWidth={1560}
              icon={<i className="sv sv-plus-square {font-size:1.5rem;}" />}
              name="CREATE"
              variant="outline-secondary-subtle"
              onClick={onCreate}
            >
              Create Accident
            </FormButton>
          </QueryFilters.Control>
          <QueryFilters.Input>
            <PageNavigationDropdown />
          </QueryFilters.Input>
          <QueryFilters.Control>
            <FormButton
              icon={<i className="sv sv-trash2 {font-size:1.5rem;}" />}
              name="DELETE"
              variant="outline-secondary-subtle"
              onClick={onDelete}
              disabled={selected.size === 0}
            />
          </QueryFilters.Control>
          <QueryFilters.Control>
            <FormButton
              icon={<i className="sv sv-excel {font-size:1.5rem;}" />}
              name="EXPORT_AS_CSV"
              variant="outline-secondary-subtle"
              onClick={onExportToCSV}
              feedback="Export to CSV"
              disabled={accidents.size === 0}
            />
          </QueryFilters.Control>
          {/* <QueryFilters.Control>
            <FormButton
              variant="outline-secondary-subtle"
              onClick={onOpenSettings}
              icon={<i className="sv sv-cog {font-size:1.5rem;}" />}
              feedback="Accident History Page Settings & Preferences"
            />
          </QueryFilters.Control> */}
        </div>
      </QueryFilters>
      <AccidentHistoryFiltersInfo />
    </>
  );
};

export default AccidentHistoryFilters;
